/* ============================================================================
   WAKA — PDP board size picker (no framework)
   Drives the "Size & specification" section: weight + discipline + skill →
   a Performance / Light-wind pair, with a kg⇄lb unit switch that converts
   every figure (incl. the rider-weight column).

   SOURCE OF TRUTH FOR SIZING LOGIC
   --------------------------------
   The recommendation tables come from the knowledge base
   (waka-agent/functions/src/knowledge-base.ts → "Board Selection by Weight" /
   "Sizing for Parawinging"). This file does NOT want its own private copy.
   It calls  window.WakaSizeCalc.recommend(kg, discipline, skill)  when that
   module (assets/waka-size-calc.js, the KB-synced calculator) is present.

   The MIRROR object below is ONLY a fallback so the section renders if
   waka-size-calc.js hasn't loaded. Per KB §8 (design-system SIZING-LOGIC.md §8):
   if sizing changes, update all five copies together — the KB, waka-size-calc.js,
   this mirror, waka-agent/functions/src/size-calc.ts, and viento.js's
   WING_FALLBACK — do not hand-edit this mirror in isolation.
   ========================================================================== */
(function () {
  'use strict';

  var KG_LB = 2.2046;

  /* ---- fallback mirror of the KB tables (Performance-first, Light-wind-second) ---- */
  var MIRROR = {
    wing: {
      small: ['66L'],
      b0: { 0: ['77L','88L'], 1: ['66L','77L'], 2: ['66L'] },           // 55–65
      b1: { 0: ['88L','99L'], 1: ['77L','88L'], 2: ['66L','77L'] },     // 65–75
      b2: { 0: ['99L','111L'],1: ['88L','99L'], 2: ['77L','88L'] },     // 75–85
      b3: { 0: ['99L','111L'],1: ['88L','99L'], 2: ['77L','88L'] },     // 85–95
      b4: { 0: ['111L'],      1: ['99L','111L'],2: ['88L','99L'] }      // 95+
    },
    para: {
      b0: { 0: ['88L','99L'], 1: ['77L','88L'] },                       // <65
      b1: { 0: ['88L','99L'], 1: ['77L','88L'] },                       // 65–75
      b2: { 0: ['99L','111L'],1: ['88L','99L'] },                       // 75–85
      b3: { 0: ['99L','111L'],1: ['99L','111L'] },                      // 85–95
      b4: { 0: ['111L'],      1: ['99L','111L'] }                       // 95+
    }
  };

  function mirrorRecommend(kg, discipline, skill) {
    if (kg == null || isNaN(kg) || kg < 30 || kg > 200) return { state: 'empty' };
    if (kg > 115) return { state: 'over' };
    if (discipline === 'wing') {
      var cell;
      if (kg < 55) cell = MIRROR.wing.small;
      else if (kg < 65) cell = MIRROR.wing.b0[skill];
      else if (kg < 75) cell = MIRROR.wing.b1[skill];
      else if (kg < 85) cell = MIRROR.wing.b2[skill];
      else if (kg < 95) cell = MIRROR.wing.b3[skill];
      else cell = MIRROR.wing.b4[skill];
      return { state: 'ok', picks: cell.slice(), borderline: (kg >= 85 && kg < 95 && skill === 1) };
    }
    var ps = Math.min(skill, 1);
    var c;
    if (kg < 65) c = MIRROR.para.b0[ps];
    else if (kg < 75) c = MIRROR.para.b1[ps];
    else if (kg < 85) c = MIRROR.para.b2[ps];
    else if (kg < 95) c = MIRROR.para.b3[ps];
    else c = MIRROR.para.b4[ps];
    return { state: 'ok', picks: c.slice(), borderline: false };
  }

  function recommend(kg, discipline, skill) {
    if (window.WakaSizeCalc && typeof window.WakaSizeCalc.recommend === 'function') {
      return window.WakaSizeCalc.recommend(kg, discipline, skill);
    }
    return mirrorRecommend(kg, discipline, skill);
  }

  /* ------------------------------------------------------------------ */
  var root = document.getElementById('WakaSizePicker');
  if (!root) return;

  var wInput  = root.querySelector('[data-size-weight]');
  var wUnit   = root.querySelector('[data-size-weight-unit]');
  var discBtns = root.querySelectorAll('[data-disc]');
  var skillWrap = root.querySelector('[data-skill-group]');
  var skillLabel = root.querySelector('[data-skill-label]');
  var paraHint = root.querySelector('[data-para-hint]');
  var resultBox = root.querySelector('[data-size-result]');
  var unitBtns = root.querySelectorAll('[data-unit]');
  var rows = root.querySelectorAll('tr[data-vol]');
  // lead paragraph link sits ABOVE the picker, so it's queried from the section
  var section = root.closest('.wsp');
  var leadLink = section && section.querySelector('.wsp-lead [data-viento-ask]');
  var leadDefault = leadLink ? leadLink.getAttribute('data-viento-ask') : '';

  var WING_SKILLS = ['Beginner', 'Intermediate', 'Advanced'];
  var PARA_SKILLS = ['Learning', 'Proficient'];

  var state = { metric: root.getAttribute('data-default-unit') !== 'imperial', disc: 'wing', skill: 1, weight: '' };

  function round(n) { return Math.round(n); }
  function kgValue() {
    if (state.weight === '') return null;
    var v = parseFloat(state.weight);
    if (isNaN(v)) return null;
    return state.metric ? v : v / KG_LB;
  }

  /* ---- Viento handoff: carry the picker's context into the chat so the rider
         doesn't re-enter weight/skill — Viento can go straight to the next
         questions (where they ride, conditions, foil pairing). ---- */
  function escAttr(s) { return String(s).replace(/&/g, '&amp;').replace(/"/g, '&quot;'); }
  function skillName() {
    var labels = state.disc === 'wing' ? WING_SKILLS : PARA_SKILLS;
    var i = state.disc === 'wing' ? state.skill : Math.min(state.skill, 1);
    return labels[i] || '';
  }
  function profileText(rec) {
    if (kgValue() == null) return '';
    var w = state.weight + ' ' + (state.metric ? 'kg' : 'lb');
    var disc = state.disc === 'para' ? 'parawinging' : 'winging';
    var s = "I used the size guide — I'm " + w + ', ' + disc + ', ' + skillName().toLowerCase() + '.';
    if (rec && rec.state === 'ok' && rec.picks && rec.picks.length) {
      s += rec.picks.length === 1
        ? ' It suggested the ' + rec.picks[0] + '.'
        : ' It suggested ' + rec.picks[0] + ' (performance) and ' + rec.picks[1] + ' (light wind).';
    } else if (rec && rec.state === 'over') {
      s += " That's above the lineup.";
    }
    return s;
  }
  function nextStepAsk(rec) {
    var p = profileText(rec);
    return (p ? p + ' ' : '') + 'Take it from here — ask me about where I ride, the conditions and foil pairing to dial in the final pick.';
  }

  /* ---- skill control: 3 levels for winging, 2 for parawinging ---- */
  function renderSkill() {
    var labels = state.disc === 'wing' ? WING_SKILLS : PARA_SKILLS;
    var eff = state.disc === 'wing' ? state.skill : Math.min(state.skill, 1);
    skillLabel.textContent = state.disc === 'wing' ? 'Skill level' : 'Parawing skill';
    skillWrap.innerHTML = '';
    labels.forEach(function (lab, i) {
      var b = document.createElement('button');
      b.type = 'button';
      b.className = 'wsp-seg-btn' + (eff === i ? ' on' : '');
      b.textContent = lab;
      b.addEventListener('click', function () { state.skill = i; renderSkill(); update(); });
      skillWrap.appendChild(b);
    });
    paraHint.hidden = state.disc !== 'para';
  }

  /* ---- table cell conversion ---- */
  function renderTable(roles) {
    rows.forEach(function (tr) {
      var vol = tr.getAttribute('data-vol');
      var lenCm = tr.getAttribute('data-len-cm');
      var lenFt = tr.getAttribute('data-len-ft');
      var widIn = parseFloat(tr.getAttribute('data-wid-in'));
      var wtKg  = parseFloat(tr.getAttribute('data-wt-kg'));
      var rLo   = parseFloat(tr.getAttribute('data-rider-lo'));
      var rHi   = parseFloat(tr.getAttribute('data-rider-hi'));

      var cLen = tr.querySelector('[data-cell="len"]');
      var cWid = tr.querySelector('[data-cell="wid"]');
      var cWt  = tr.querySelector('[data-cell="wt"]');
      var cRid = tr.querySelector('[data-cell="rider"]');

      if (cLen) cLen.textContent = state.metric ? lenCm + ' cm' : lenFt;
      if (cWid) cWid.textContent = state.metric ? round(widIn * 2.54) + ' cm' : widIn + '"';
      if (cWt)  cWt.textContent  = state.metric ? wtKg.toFixed(1) + ' kg' : (wtKg * KG_LB).toFixed(1) + ' lb';
      if (cRid) {
        var u = state.metric ? 'kg' : 'lb';
        var lo = state.metric ? rLo : round(rLo * KG_LB);
        var hi = state.metric ? rHi : round(rHi * KG_LB);
        cRid.textContent = rLo === 0 ? 'Under ' + hi + ' ' + u
          : (rHi >= 999 ? lo + ' ' + u + '+' : lo + '\u2013' + hi + ' ' + u);
      }

      // matched-row roles
      tr.classList.remove('perf', 'light');
      var tag = tr.querySelector('[data-role-tag]');
      if (tag) tag.remove();
      var role = roles && roles[vol];
      if (role) {
        tr.classList.add(role);
        var span = document.createElement('span');
        span.className = 'wsp-rtag ' + role;
        span.setAttribute('data-role-tag', '');
        span.textContent = role === 'perf' ? 'Performance' : 'Light wind';
        var vcell = tr.querySelector('[data-cell="vol"]');
        if (vcell) vcell.appendChild(span);
      }
    });
  }

  /* ---- result strip ---- */
  function renderResult(rec) {
    var html = '<span class="wsp-cap">Your match</span>';
    if (rec.state === 'empty') {
      html += '<div class="wsp-empty">Enter your weight</div>';
    } else if (rec.state === 'over') {
      html += '<div class="wsp-empty">Above the lineup</div>'
        + '<p class="wsp-viento">Over ~115 kg sits beyond the largest Glide (111L). '
        + '<a class="wsp-ilink" data-viento-ask="' + escAttr(profileText(rec) + ' Which Glide setup suits me?') + '">Talk to Viento \u2192</a> '
        + 'we\u2019ll sort the right setup with you.</p>';
    } else {
      html += '<div class="wsp-pair">';
      if (rec.picks.length === 1) {
        html += pick('perf', rec.picks[0], 'Your size', 'The single best fit for your weight and skill.');
      } else {
        html += pick('perf', rec.picks[0], 'Performance', 'Smaller \u2014 livelier, stronger wind.');
        html += pick('light', rec.picks[1], 'Light wind', 'Larger \u2014 easier, lighter days.');
      }
      html += '</div>';
      if (rec.borderline) {
        html += '<div class="wsp-note">Around 85\u201390 kg this is a close call \u2014 the smaller board rides livelier in '
          + 'wind, the larger is the easier, more stable pick. '
          + '<a class="wsp-ilink wsp-match" role="button">Viento can settle it \u2192</a></div>';
      }
      html += '<p class="wsp-viento">Riding a discipline we haven\u2019t asked about, or pairing foils? '
        + '<a class="wsp-ilink wsp-match" role="button">Refine with Viento \u2192</a></p>';
    }
    resultBox.innerHTML = html;
    // Phase 1: the match handoff opens the recap + compare panes instantly (no proxy round-trip)
    Array.prototype.forEach.call(resultBox.querySelectorAll('.wsp-match'), function (a) {
      a.style.cursor = 'pointer';
      a.addEventListener('click', function (e) { e.preventDefault(); openVientoMatch(rec); });
    });
  }

  function pick(role, vol, label, cond) {
    return '<div class="wsp-pick ' + role + '"><span class="wsp-vol">' + vol + '</span>'
      + '<span class="wsp-meta"><span class="wsp-role">' + label + '</span>'
      + '<span class="wsp-cond">' + cond + '</span></span></div>';
  }

  /* ---- Viento handoff: build the recap + compare panes (Phase 1) ---- */
  function specsFor(vol) {
    var tr = root.querySelector('tr[data-vol="' + vol + '"]');
    if (!tr) return [];
    var lenCm = tr.getAttribute('data-len-cm'), lenFt = tr.getAttribute('data-len-ft');
    var rLo = parseFloat(tr.getAttribute('data-rider-lo')), rHi = parseFloat(tr.getAttribute('data-rider-hi'));
    var len = state.metric ? (lenCm ? lenCm + ' cm' : (lenFt || '—')) : (lenFt || '—');
    var u = state.metric ? 'kg' : 'lb';
    var lo = state.metric ? rLo : Math.round(rLo * KG_LB);
    var hi = state.metric ? rHi : Math.round(rHi * KG_LB);
    var rider = (isNaN(rLo) || isNaN(rHi)) ? '—'
      : (rLo === 0 ? 'Under ' + hi + ' ' + u : (rHi >= 999 ? lo + ' ' + u + '+' : lo + '–' + hi + ' ' + u));
    return [['Length', len], ['Rider', rider]];
  }
  function buildMatchParts(rec) {
    var unit = state.metric ? 'kg' : 'lb';
    var profile = {
      weight: (state.weight || '—') + ' ' + unit,
      discipline: state.disc === 'para' ? 'Parawinging' : 'Winging',
      skill: skillName(),
      spot: '—'
    };
    var parts = [{ type: 'recap', profile: profile }];
    if (rec.picks.length === 1) {
      parts.push({ type: 'compare', header: 'Your size', picks: [
        { vol: rec.picks[0], role: 'Your size', tone: 'perf', why: 'The single best fit for your weight and skill.', specs: specsFor(rec.picks[0]) }
      ] });
    } else {
      parts.push({ type: 'compare', header: 'Your two-board match', picks: [
        { vol: rec.picks[0], role: 'Performance', tone: 'perf', why: 'Smaller — livelier, stronger wind.', specs: specsFor(rec.picks[0]) },
        { vol: rec.picks[1], role: 'Light wind', tone: 'light', why: 'Larger — easier, lighter days.', specs: specsFor(rec.picks[1]) }
      ] });
    }
    var base = profileText(rec);
    parts.push({ type: 'text', text: 'To dial it to one, tell me a bit more — these three decide it.' });
    parts.push({ type: 'chips', options: [
      { label: 'Where I ride', ask: base + ' Help me factor in where I usually ride.' },
      { label: 'My conditions', ask: base + ' Factor in my usual wind and conditions.' },
      { label: 'Foils I own', ask: base + ' I already own foils — what setup works with these?' }
    ] });
    return parts;
  }
  function openVientoMatch(rec) {
    if (!window.Viento) return;
    if (rec.state === 'ok' && window.Viento.seedParts) { window.Viento.seedParts(buildMatchParts(rec)); return; }
    if (window.Viento.ask) window.Viento.ask(profileText(rec) || 'Help me pick a Glide volume.');
  }

  /* ---- main update ---- */
  function update() {
    var kg = kgValue();
    var rec = recommend(kg, state.disc, state.skill);
    var roles = null;
    if (rec.state === 'ok') {
      roles = {};
      if (rec.picks.length === 1) { roles[rec.picks[0]] = 'perf'; }
      else { roles[rec.picks[0]] = 'perf'; roles[rec.picks[1]] = 'light'; }
    }
    renderResult(rec);
    renderTable(roles);
    if (leadLink) {
      var p = profileText(rec);
      leadLink.setAttribute('data-viento-ask', p ? nextStepAsk(rec) : leadDefault);
    }
  }

  /* ---- wiring ---- */
  wInput.addEventListener('input', function (e) {
    state.weight = e.target.value.replace(/[^0-9.]/g, '');
    if (state.weight !== e.target.value) e.target.value = state.weight;
    update();
  });

  discBtns.forEach(function (b) {
    b.addEventListener('click', function () {
      state.disc = b.getAttribute('data-disc');
      discBtns.forEach(function (x) { x.classList.toggle('on', x === b); });
      renderSkill();
      update();
    });
  });

  unitBtns.forEach(function (b) {
    b.addEventListener('click', function () {
      var toMetric = b.getAttribute('data-unit') === 'metric';
      if (toMetric !== state.metric && state.weight !== '') {
        var v = parseFloat(state.weight);
        if (!isNaN(v)) {
          state.weight = String(round(toMetric ? v / KG_LB : v * KG_LB));
          wInput.value = state.weight;
        }
      }
      state.metric = toMetric;
      wUnit.textContent = toMetric ? 'kg' : 'lb';
      unitBtns.forEach(function (x) { x.classList.toggle('on', x === b); });
      update();
    });
  });

  // init unit-dependent chrome
  wUnit.textContent = state.metric ? 'kg' : 'lb';
  unitBtns.forEach(function (x) { x.classList.toggle('on', x.getAttribute('data-unit') === (state.metric ? 'metric' : 'imperial')); });
  renderSkill();
  update();
})();
